/**
 * Settings → MCP (ENG-74).
 *
 * Two jobs, in the order they're needed: give the admin the connector URL and
 * the exact steps to add it, then show who has connected once people start.
 *
 * The URL comes from the backend rather than being assembled here. Building it
 * in React would let this page display a URL we don't actually answer on, and
 * the failure would be a customer pasting something that 404s with nothing on
 * our side to notice.
 */
import { useEffect, useState } from 'react';
import ApiService from '@/services/api.service';
import { Button } from '@/components/ui/button';
import { useApiAuthReady } from '@/hooks/useApiAuthReady';
import { FeatureFlag, useFeatureFlagResolved } from '@/services/featureFlag.service';
import McpDisabledNotice from '@/components/McpDisabledNotice';
import { Check, Copy, ExternalLink, Loader2 } from 'lucide-react';

type Grant = { scope: string; description: string };
type ConnectorInfo = {
  account_id: string;
  account_name: string;
  connector_url: string;
  connector_name: string;
  install_url: string;
  grants: Grant[];
  error?: string;
};
type Session = {
  id: string;
  email: string | null;
  client_name: string | null;
  connected_at: string | null;
  last_used_at: string | null;
  is_live: boolean;
};

const PAGE_SIZE = 25;

export default function McpTab() {
  const [info, setInfo] = useState<ConnectorInfo | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auth arrives asynchronously. Firing on mount sends the request with no
  // Authorization header and 401s even though the user is signed in — the
  // problem this gate exists to solve.
  // Gate on what ApiService will actually send, not on the auth context. Those
  // two disagree on a cold load — the context has a user before the token
  // reaches the singleton ApiService reads — which is why this 401'd on direct
  // navigation but worked when reached from inside the SPA.
  // Gate on what ApiService will actually send, not on the auth context. The two
  // disagree on a cold load — the context has a user before the token reaches
  // the singleton ApiService reads — which is why this 401'd on direct
  // navigation and worked when reached from inside the SPA.
  const apiReady = useApiAuthReady();

  // `useFeatureFlagResolved`, not `useFeatureFlag`: the resolved variant reports
  // isLoading, so a cold load shows a spinner rather than flashing "not enabled"
  // at an account that has it before LaunchDarkly reports in.
  const { value: mcpEnabled, isLoading: flagLoading } = useFeatureFlagResolved(
    FeatureFlag.ALLOW_MCP,
    false,
  );

  useEffect(() => {
    // Nothing is fetched for an account without the feature — no connector URL
    // to show, and no reason to ask the API for one.
    if (!apiReady || !mcpEnabled) return;
    ApiService.getInstance()
      .request('GET', '/mcp/connector-info')
      .then((data: any) => {
        // The endpoint can answer with an { error } shape instead of a
        // connector. Treat anything without a URL as an error rather than
        // rendering it — `grants` would be undefined and the tab would crash
        // on .map, taking the whole Settings page with it.
        if (data?.connector_url) setInfo(data as ConnectorInfo);
        else setError(data?.error || 'Could not load connector details for this account.');
      })
      .catch((e: any) => setError(e?.message || 'Could not load connector details.'));
  }, [apiReady, mcpEnabled]);

  const loadSessions = (nextOffset: number) => {
    setLoading(true);
    ApiService.getInstance()
      .request('GET', '/mcp/sessions', { offset: nextOffset, limit: PAGE_SIZE })
      .then((data: any) => {
        setSessions(data.sessions || []);
        setTotal(data.total || 0);
        setOffset(nextOffset);
      })
      .catch((e: any) => setError(e?.message || 'Could not load connected agents.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (apiReady && mcpEnabled) loadSessions(0);
  }, [apiReady, mcpEnabled]);

  const copy = () => {
    if (!info?.connector_url) return;
    navigator.clipboard.writeText(info.connector_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const signOut = async (id: string) => {
    await ApiService.getInstance().request('POST', `/mcp/sessions/${id}/sign-out`, {});
    loadSessions(offset);
  };

  const signOutAll = async () => {
    await ApiService.getInstance().request('POST', '/mcp/sessions/sign-out-all', {});
    loadSessions(0);
  };

  // Before error/loading: an account without the feature should never see a
  // connector error, only the explanation.
  if (flagLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (!mcpEnabled) {
    return <McpDisabledNotice context="settings" />;
  }

  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (!info) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="max-w-3xl space-y-8">
      {/* ── Connect ─────────────────────────────────────────────── */}
      <section className="space-y-3">
        <div>
          <h2 className="text-base font-semibold">Connect Claude to {info.account_name}</h2>
          <p className="text-sm text-muted-foreground">
            Claude can query your metrics and knowledge base directly. Add this connector once,
            and everyone on your team signs in with their own Pliable account.
          </p>
        </div>

        <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
          <code className="flex-1 truncate text-sm">{info.connector_url}</code>
          <Button variant="ghost" size="sm" className="shrink-0 gap-1.5" onClick={copy}>
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>

        {/* The whole org-wide setup in one click. Claude opens its add-connector
            dialog with the name and URL already filled in; the admin reviews and
            confirms. The manual steps stay below for anyone the link doesn't
            suit — a different Claude account, a copied link, an admin who wants
            to see what they're agreeing to first. */}
        {info.install_url && (
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <Button asChild>
              <a href={info.install_url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4" />
                Add to Claude
              </a>
            </Button>
            <span className="text-xs text-muted-foreground">
              Opens Claude's organization connector settings. You'll confirm before
              anything is added.
            </span>
          </div>
        )}

        <div className="space-y-1.5 text-sm">
          <p className="font-medium">Or add it by hand (Team or Enterprise)</p>
          <ol className="list-decimal space-y-1 pl-5 text-muted-foreground">
            <li>Go to Organization settings → Connectors</li>
            <li>Select Add, then Custom. If asked for a type, choose Web</li>
            <li>Paste the URL above</li>
            {/* The single most likely thing for an admin to fill in
                unnecessarily — Claude identifies itself without it. */}
            <li>
              Leave the OAuth Client ID and Secret <span className="font-medium">empty</span>
            </li>
            <li>Select Add</li>
          </ol>
          <p className="pt-1 text-muted-foreground">
            Each person then goes to Customize → Connectors and selects Connect. They sign in
            with Pliable once, and see only the data their own account allows.
          </p>
        </div>

        <div className="space-y-1.5 text-sm">
          <p className="font-medium">In Claude Code</p>
          <code className="block overflow-x-auto rounded-md border bg-muted/40 px-3 py-2 text-xs">
            claude mcp add --transport http pliable {info.connector_url}
          </code>
        </div>

        <div className="space-y-1.5 text-sm">
          <p className="font-medium">What this allows</p>
          <ul className="space-y-1 text-muted-foreground">
            {(info.grants ?? []).map((g) => (
              <li key={g.scope}>· {g.description}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Connected agents ────────────────────────────────────── */}
      <section className="space-y-3">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="text-base font-semibold">Connected agents</h2>
            <p className="text-sm text-muted-foreground">
              {total === 0
                ? 'Nobody has connected an agent yet.'
                : `${total} active ${total === 1 ? 'connection' : 'connections'}.`}
            </p>
          </div>
          {total > 0 && (
            <Button variant="outline" size="sm" onClick={signOutAll}>
              Sign out all
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        ) : sessions.length > 0 ? (
          <>
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/40 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">Person</th>
                    <th className="px-3 py-2 font-medium">Client</th>
                    <th className="px-3 py-2 font-medium">Connected</th>
                    <th className="px-3 py-2 font-medium">Last used</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => (
                    <tr key={s.id} className="border-b last:border-0">
                      <td className="px-3 py-2">{s.email || 'Unknown'}</td>
                      <td className="px-3 py-2 text-muted-foreground">{s.client_name || '—'}</td>
                      <td className="px-3 py-2 text-muted-foreground">{formatDate(s.connected_at)}</td>
                      <td className="px-3 py-2 text-muted-foreground">{formatDate(s.last_used_at)}</td>
                      <td className="px-3 py-2 text-right">
                        <Button variant="ghost" size="sm" onClick={() => signOut(s.id)}>
                          Sign out
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {total > PAGE_SIZE && (
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={offset === 0}
                    onClick={() => loadSessions(Math.max(0, offset - PAGE_SIZE))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={offset + PAGE_SIZE >= total}
                    onClick={() => loadSessions(offset + PAGE_SIZE)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : null}

        {/* Said plainly because an admin will otherwise reach for Sign out
            expecting it to lock someone out. It ends the session; it does not
            remove access, and the person can reconnect immediately. */}
        <p className="text-xs text-muted-foreground">
          Signing out disconnects an agent, but the person can connect again. To remove
          someone's access to this account entirely, remove them under Users.
        </p>
      </section>
    </div>
  );
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}
